<template>
  <div class="detail-view">
    <div class="detail-view-intro">
      <h1 class="detail-view-title">Respostas da Azul</h1>
    </div>

    <p v-if="loading" class="detail-view-loading">Carregando respostas…</p>

    <template v-else>
      <div class="detail-stats detail-stats--4col">
        <StatTile
          label="Aplicadas pelo agente"
          :value="contagens.aplicadas"
          icon="mdi-check-decagram-outline"
          color="success"
        />
        <StatTile
          label="Avisos para a equipe"
          :value="contagens.avisos"
          icon="mdi-email-alert-outline"
          color="warning"
        />
        <StatTile
          label="Já resolvidas pela equipe"
          :value="contagens.equipe"
          icon="mdi-account-check-outline"
          color="secondary"
        />
        <StatTile
          label="Aguardando a Azul"
          :value="contagens.aguardando"
          icon="mdi-timer-sand"
          color="neutral"
        />
      </div>

      <div class="resp-tabs">
        <button
          v-for="t in tabs"
          :key="t.key"
          class="resp-tab"
          :class="{ 'is-active': tab === t.key }"
          @click="selecionar(t.key)"
        >
          {{ t.label }}
          <span v-if="t.total !== null" class="resp-tab-n">{{ t.total }}</span>
        </button>
        <input
          v-if="tab !== 'aguardando'"
          v-model="busca"
          class="resp-busca"
          type="search"
          placeholder="Buscar chamado, voucher ou reserva"
          @keyup.enter="carregar"
        />
      </div>

      <p v-if="erro" class="detail-empty">{{ erro }}</p>

      <!-- Aguardando a Azul -->
      <template v-else-if="tab === 'aguardando'">
        <div v-if="aguardando.length" class="resp-box">
          <table class="resp-table">
            <colgroup>
              <col style="width: 22%" />
              <col style="width: 30%" />
              <col style="width: 28%" />
              <col style="width: 20%" />
            </colgroup>
            <thead>
              <tr>
                <th>Chamado</th>
                <th>Reserva</th>
                <th>Aberto em</th>
                <th class="num">Dias de espera</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in aguardando" :key="a.ritm">
                <td class="mono">{{ a.ritm }}</td>
                <td>
                  <strong>{{ a.localizador || "—" }}</strong>
                  <span v-if="a.codigo_reserva" class="muted">
                    · {{ a.codigo_reserva }}</span
                  >
                </td>
                <td>{{ fmt(a.aberto_em) }}</td>
                <td class="num">
                  <span :class="{ 'resp-atraso': (a.dias || 0) >= 10 }">{{
                    a.dias === null ? "—" : a.dias
                  }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="detail-empty">
          Nenhum chamado aguardando resposta da Azul.
        </p>
      </template>

      <!-- Respostas -->
      <template v-else>
        <div v-if="itens.length" class="resp-box">
          <table class="resp-table">
            <colgroup>
              <col style="width: 17%" />
              <col style="width: 29%" />
              <col style="width: 36%" />
              <col style="width: 11%" />
              <col v-if="comAcao" style="width: 7%" />
            </colgroup>
            <thead>
              <tr>
                <th>Chamado</th>
                <th>E-mail da Azul</th>
                <th>Resultado</th>
                <th>Recebido</th>
                <th v-if="comAcao"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="r in itens">
                <tr :key="r.id" class="resp-row" @click="abrir(r.id)">
                  <td>
                    <div class="mono">{{ r.ritm }}</div>
                    <div class="resp-reserva">
                      <strong>{{ r.voucher || "—" }}</strong>
                      <span v-if="r.codigo_reserva" class="muted">
                        · {{ r.codigo_reserva }}</span
                      >
                    </div>
                  </td>
                  <td>
                    <span class="resp-tipo">{{ tipoLabel(r.tipo) }}</span>
                    <div class="resp-resumo">{{ r.resumo }}</div>
                  </td>
                  <td>
                    <span
                      class="resp-decisao"
                      :class="`resp-decisao--${r.decisao}`"
                      >{{ decisaoLabel(r.decisao) }}</span
                    >
                    <div v-if="r.motivo" class="resp-motivo">
                      {{ r.motivo }}
                    </div>
                    <div v-if="r.alterado_por" class="resp-motivo">
                      Última alteração de {{ r.alterado_por }}
                      <template v-if="r.alterado_em"
                        >em {{ fmt(r.alterado_em) }}</template
                      >
                    </div>
                    <ul
                      v-if="(r.campos_alterados || []).length"
                      class="resp-campos"
                    >
                      <li v-for="(c, k) in r.campos_alterados" :key="k">
                        <span class="resp-campo-nome">{{ c.rotulo }}</span>
                        <span class="muted"> · {{ c.servico_nome }}</span>
                        <div>
                          <s class="muted">{{ c.de }}</s> →
                          <strong>{{ c.para }}</strong>
                        </div>
                      </li>
                    </ul>
                  </td>
                  <td class="muted">
                    {{ fmt(r.recebido_at || r.registrado_at) }}
                  </td>
                  <td v-if="comAcao" @click.stop>
                    <button
                      class="resp-visto"
                      @click="marcarVisto(r, !r.visto_at)"
                    >
                      {{ r.visto_at ? "Reabrir" : "Visto" }}
                    </button>
                  </td>
                </tr>
                <tr
                  v-if="aberto === r.id"
                  :key="`d${r.id}`"
                  class="resp-detalhe"
                >
                  <td :colspan="comAcao ? 5 : 4">
                    <pre class="resp-texto">{{
                      r.solucao || "(sem texto)"
                    }}</pre>
                    <div v-if="r.visto_por" class="muted">
                      Visto por {{ r.visto_por }} em {{ fmt(r.visto_at) }}
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <p v-else class="detail-empty">{{ vazio }}</p>
      </template>
    </template>
  </div>
</template>

<script>
import {
  getAzulRespostas,
  getAzulRespostasAguardando,
  marcarAzulRespostaVista,
  azulErro,
} from "@/services/azulApi.js";
import StatTile from "@/components/StatTile/StatTile.vue";

const DECISAO_LABEL = {
  aplicada: "Aplicada no Phoenix",
  simulada: "Simulação",
  resolvida_equipe: "Já resolvida pela equipe",
  sem_dado: "Só informativo",
  nao_identificado: "Agente não soube aplicar",
  sem_reserva: "Reserva indisponível",
  erro: "Erro",
};
const TIPO_LABEL = {
  voo: "Voo",
  hotel: "Hotel",
  telefone: "Telefone",
  data: "Data",
  misto: "Voo e hotel",
  informativo: "Informativo",
  nao_identificado: "Texto livre",
};
const VAZIO = {
  aplicadas: "Nenhuma resposta aplicada ainda.",
  avisos: "Nenhum aviso aberto.",
  vistos: "Nenhum aviso marcado como visto.",
  equipe: "Nenhuma resposta para reservas já resolvidas pela equipe.",
  todas: "Nenhuma resposta da Azul registrada ainda.",
};

// "2026-10-16 11:04:02" -> "16/10/2026 11:04"
function formatarData(valor) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(
    String(valor || "")
  );
  if (!m) return valor || "—";
  return `${m[3]}/${m[2]}/${m[1]}${m[4] ? ` ${m[4]}:${m[5]}` : ""}`;
}

// Respostas por e-mail da Azul aos chamados que o agente abriu (tabela azul_respostas).
export default {
  name: "AzulRespostas",
  components: { StatTile },
  props: {
    base: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      erro: "",
      tab: "avisos",
      busca: "",
      itens: [],
      aguardando: [],
      contagens: { aplicadas: 0, avisos: 0, equipe: 0, aguardando: 0 },
      aberto: null,
    };
  },
  computed: {
    tabs() {
      const c = this.contagens;
      return [
        { key: "avisos", label: "Avisos para a equipe", total: c.avisos },
        { key: "aplicadas", label: "Aplicadas", total: c.aplicadas },
        { key: "equipe", label: "Já resolvidas pela equipe", total: c.equipe },
        { key: "aguardando", label: "Aguardando a Azul", total: c.aguardando },
        { key: "vistos", label: "Vistos", total: null },
        { key: "todas", label: "Todas", total: null },
      ];
    },
    vazio() {
      return VAZIO[this.tab] || VAZIO.todas;
    },
    // o botao "Visto" so existe nas abas de aviso
    comAcao() {
      return this.tab === "avisos" || this.tab === "vistos";
    },
  },
  watch: {
    base() {
      this.aberto = null;
      this.carregar();
    },
  },
  created() {
    this.carregar();
  },
  methods: {
    fmt: formatarData,
    decisaoLabel: (d) => DECISAO_LABEL[d] || d,
    tipoLabel: (t) => TIPO_LABEL[t] || t,
    selecionar(key) {
      this.tab = key;
      this.aberto = null;
      this.carregar();
    },
    abrir(id) {
      this.aberto = this.aberto === id ? null : id;
    },
    async carregar() {
      this.loading = !this.itens.length && !this.aguardando.length;
      this.erro = "";
      try {
        if (this.tab === "aguardando") {
          const [ag, lista] = await Promise.all([
            getAzulRespostasAguardando(this.base),
            getAzulRespostas(this.base, { grupo: "aplicadas", limit: 1 }),
          ]);
          this.aguardando = ag.itens;
          this.contagens = lista.contagens;
        } else {
          const r = await getAzulRespostas(this.base, {
            grupo: this.tab,
            q: this.busca,
          });
          this.itens = r.itens;
          this.contagens = r.contagens;
        }
      } catch (e) {
        this.erro = azulErro(e, "Não consegui carregar as respostas da Azul");
      } finally {
        this.loading = false;
      }
    },
    async marcarVisto(r, visto) {
      try {
        await marcarAzulRespostaVista(r.id, visto);
        await this.carregar();
      } catch (e) {
        this.erro = azulErro(e, "Não consegui marcar o aviso como visto");
      }
    },
  },
};
</script>

<style src="../PendenciesDetail/styles.scss" scoped lang="scss" />
<style scoped lang="scss">
.detail-stats--4col {
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
.resp-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.resp-tab {
  border: 1px solid #d9dee4;
  background: #fff;
  border-radius: 18px;
  padding: 6px 14px;
  font-size: 0.82rem;
  color: #3b4651;
  cursor: pointer;

  &.is-active {
    background: #0f7a6e;
    border-color: #0f7a6e;
    color: #fff;
  }
}
.resp-tab-n {
  margin-left: 6px;
  font-weight: 700;
}
.resp-busca {
  margin-left: auto;
  width: 280px;
  border: 1px solid #d9dee4;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 0.82rem;
  background: #fff;
}

// Tabela propria: largura fixa por coluna, texto quebra (sem rolagem para o lado).
.resp-box {
  background: #fff;
  border: 1px solid #e4e8ec;
  border-radius: 12px;
  overflow: hidden;
}
.resp-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.85rem;
  color: #1f2933;

  th {
    text-align: left;
    padding: 12px 16px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #8a929b;
    border-bottom: 1px solid #e4e8ec;
    background: #fafbfc;
  }
  td {
    padding: 14px 16px;
    vertical-align: top;
    border-bottom: 1px solid #eef1f4;
    word-break: break-word;
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
  .num {
    text-align: right;
  }
}
.resp-row {
  cursor: pointer;

  &:hover td {
    background: #f8fafb;
  }
}
.resp-reserva {
  margin-top: 3px;
  font-size: 0.8rem;
}
.resp-tipo {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  background: #eef2f6;
  color: #55606c;
}
.resp-resumo {
  margin-top: 6px;
  line-height: 1.4;
}
.resp-decisao {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #eef2f6;
  color: #55606c;

  &--aplicada {
    background: #dcf3ee;
    color: #0f7a6e;
  }
  &--simulada,
  &--resolvida_equipe {
    background: #e6f0fb;
    color: #2f7fd1;
  }
  &--sem_dado,
  &--nao_identificado,
  &--sem_reserva {
    background: #fdf0dc;
    color: #b06a12;
  }
  &--erro {
    background: #fbe3e3;
    color: #c81e22;
  }
}
.resp-motivo {
  margin-top: 6px;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #55606c;
}
.resp-campos {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  font-size: 0.82rem;
  line-height: 1.45;

  li + li {
    margin-top: 6px;
  }
}
.resp-campo-nome {
  font-weight: 600;
}
.resp-detalhe td {
  background: #f8fafb;
}
.resp-texto {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0 0 6px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e4e8ec;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.82rem;
  max-height: 260px;
  overflow: auto;
}
.resp-visto {
  border: 1px solid #d9dee4;
  background: #fff;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.75rem;
  cursor: pointer;
  white-space: nowrap;
}
.resp-atraso {
  color: #c81e22;
  font-weight: 700;
}
.mono {
  font-family: monospace;
  font-size: 0.8rem;
  word-break: keep-all;
}
.muted {
  color: #8a929b;
}
</style>
